import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Building2, CreditCard, FileText, Download, 
  TrendingUp, UserCheck, Shield, LogOut, RefreshCw, 
  DollarSign, Activity, Calendar, CheckCircle, XCircle
} from 'lucide-react';
import authService from '../utils/authService';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    // Only check authentication once on mount
    if (hasCheckedAuth.current) return;
    
    console.log('Checking admin authentication...');
    
    // Check if admin is authenticated
    if (!authService.isAuthenticated() || !authService.isAdmin()) {
      console.log('Not authenticated as admin, redirecting to login');
      navigate('/admin/login', { replace: true });
      return;
    }
    
    hasCheckedAuth.current = true;
    console.log('✓ Admin authenticated, loading dashboard');
    loadDashboardData();
  }, []); // Empty dependency - only run once

  const loadDashboardData = async () => {
    await fetchDashboardStats();
    if (activeTab === 'users') {
      await fetchUsers();
    } else if (activeTab === 'subscriptions') {
      await fetchSubscriptions();
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: authService.getAuthHeader()
      });
      
      setStats(response.data);
      console.log('Dashboard stats loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      
      // Only logout on actual authentication failure
      if (authService.handleAuthError(error)) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login', { replace: true });
      } else {
        toast.error('Failed to fetch statistics');
      }
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users?limit=100`, {
        headers: authService.getAuthHeader()
      });
      
      setUsers(response.data.users || []);
      console.log(`Loaded ${response.data.users?.length || 0} users`);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      
      if (!authService.handleAuthError(error)) {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/subscriptions?limit=100`, {
        headers: authService.getAuthHeader()
      });
      
      setSubscriptions(response.data.subscriptions || []);
      console.log(`Loaded ${response.data.subscriptions?.length || 0} subscriptions`);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      
      if (!authService.handleAuthError(error)) {
        toast.error('Failed to fetch subscriptions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'users' && users.length === 0) {
      fetchUsers();
    } else if (tab === 'subscriptions' && subscriptions.length === 0) {
      fetchSubscriptions();
    }
  };

  const handleLogout = () => {
    authService.clearAuth();
    toast.success('Logged out successfully');
    navigate('/admin/login', { replace: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      'ACTIVE': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      'trial': { color: 'bg-blue-100 text-blue-800', icon: Activity, label: 'Trial' },
      'expired': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Expired' },
      'cancelled': { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig['active'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getPlanBadge = (planType) => {
    const planConfig = {
      'free_trial': { color: 'bg-purple-100 text-purple-800', label: 'Free Trial' },
      'starter': { color: 'bg-blue-100 text-blue-800', label: 'Starter' },
      'pro': { color: 'bg-indigo-100 text-indigo-800', label: 'Pro' },
      'advanced': { color: 'bg-green-100 text-green-800', label: 'Advanced' }
    };
    
    const config = planConfig[planType] || { color: 'bg-gray-100 text-gray-800', label: planType };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-red-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">AutomateSEO Platform Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={loadDashboardData}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Activity },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-4 border-b-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-red-600 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_users || 0}</p>
                    <p className="text-xs text-green-600 mt-1">+{stats.recent_signups || 0} this week</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.active_subscriptions || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.trial_users || 0} trials</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Monthly Revenue (MRR)</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(stats.mrr || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">Recurring monthly</p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Expired Subscriptions</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.expired_subscriptions || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Needs attention</p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Distribution</h3>
              <div className="space-y-3">
                {stats.plan_distribution && stats.plan_distribution.length > 0 ? (
                  stats.plan_distribution.map((plan) => (
                    <div key={plan._id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getPlanBadge(plan._id)}
                        <span className="text-sm text-gray-600">{plan._id}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{plan.count} users</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No subscription data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">All Users ({users.length})</h3>
            </div>
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
              ) : users.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                                {user.full_name?.[0]?.toUpperCase() || 'U'}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.full_name || 'N/A'}</div>
                              {user.is_admin && <span className="text-xs text-red-600 font-medium">Admin</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.mobile || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.subscription ? getPlanBadge(user.subscription.plan_type) : <span className="text-sm text-gray-400">No subscription</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.subscription ? getStatusBadge(user.subscription.status) : <span className="text-sm text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                  <p className="mt-1 text-sm text-gray-500">Users will appear here once they sign up.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">All Subscriptions ({subscriptions.length})</h3>
            </div>
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
              ) : subscriptions.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sub.user_name || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{sub.user_email || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getPlanBadge(sub.plan_type)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{sub.billing_cycle || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(sub.total_amount || sub.amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(sub.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(sub.start_date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(sub.end_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
                  <p className="mt-1 text-sm text-gray-500">Subscriptions will appear here once users subscribe.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
